import { useEffect, useState } from "react";
import { api, formatTL, formatApiError } from "@/lib/api";
import { Plus, Trash2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const INCOME_CATS = [{ v: "COMMISSION", l: "Komisyon" }, { v: "RENTAL_FEE", l: "Kira Geliri" }, { v: "OTHER", l: "Diğer" }];
export const EXPENSE_CATS = [
  { v: "RENT", l: "Kira" }, { v: "UTILITIES", l: "Fatura/Elektrik/Su" },
  { v: "SALARY", l: "Maaş" }, { v: "FUEL", l: "Yakıt" },
  { v: "MARKETING", l: "Pazarlama" }, { v: "OFFICE", l: "Ofis Giderleri" }, { v: "OTHER", l: "Diğer" }
];

export default function FinancePage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ transaction_type: "INCOME", category: "COMMISSION", amount: "", description: "", date: "" });

  const load = async () => {
    const { data } = await api.get("/transactions");
    setItems(data);
    const { data: s } = await api.get("/finance/summary");
    setSummary(s);
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", { ...form, amount: parseFloat(form.amount), date: form.date ? new Date(form.date).toISOString() : null });
      toast.success("İşlem eklendi");
      setOpen(false);
      setForm({ transaction_type: "INCOME", category: "COMMISSION", amount: "", description: "", date: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/transactions/${id}`);
    load();
  };

  const catLabel = (t, c) => {
    const list = t === "INCOME" ? INCOME_CATS : EXPENSE_CATS;
    return list.find((x) => x.v === c)?.l || c;
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Muhasebe</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Gelir / Gider</h1>
          <p className="text-slate-500 mt-1">Ofis gelir giderlerini, komisyonları ve raporları yönetin.</p>
        </div>
        <Button data-testid="add-transaction-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni İşlem
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
        <Card className="p-5 bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wider mb-2"><TrendingUp className="w-3.5 h-3.5" /> Gelir</div>
          <div className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(summary.total_income)}</div>
        </Card>
        <Card className="p-5 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-700 text-xs font-semibold uppercase tracking-wider mb-2"><TrendingDown className="w-3.5 h-3.5" /> Gider</div>
          <div className="text-2xl font-bold text-red-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(summary.total_expense)}</div>
        </Card>
        <Card className="p-5 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-2"><Wallet className="w-3.5 h-3.5" /> Komisyon</div>
          <div className="text-2xl font-bold text-blue-900" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(summary.total_commission)}</div>
        </Card>
        <Card className="p-5 bg-slate-900 text-white">
          <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2"><Wallet className="w-3.5 h-3.5" /> Net Kar</div>
          <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(summary.net_profit)}</div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">İşlemler</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {items.length === 0 ? (
            <div className="p-12 text-center text-slate-400">Henüz işlem yok.</div>
          ) : items.map((t) => (
            <div key={t.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${t.transaction_type === "INCOME" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {t.transaction_type === "INCOME" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{t.description || catLabel(t.transaction_type, t.category)}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    <Badge variant="outline">{catLabel(t.transaction_type, t.category)}</Badge>
                    <span>{new Date(t.date).toLocaleDateString("tr-TR")}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                <div className={`font-bold text-sm sm:text-lg whitespace-nowrap ${t.transaction_type === "INCOME" ? "text-emerald-700" : "text-red-700"}`}>
                  {t.transaction_type === "INCOME" ? "+" : "-"}{formatTL(t.amount)}
                </div>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni İşlem Ekle</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tür</Label>
                <Select value={form.transaction_type} onValueChange={(v) => setForm({ ...form, transaction_type: v, category: v === "INCOME" ? "COMMISSION" : "RENT" })}>
                  <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INCOME">Gelir</SelectItem>
                    <SelectItem value="EXPENSE">Gider</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(form.transaction_type === "INCOME" ? INCOME_CATS : EXPENSE_CATS).map((c) => (
                      <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tutar (TL) *</Label>
              <Input data-testid="tx-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label>Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button data-testid="tx-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
